import React, { useRef, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { Footer } from './components/Footer';
import { ErrorBoundary } from './components/ErrorBoundary';
import { GrafanaDashboard } from './components/GrafanaDashboard';
import { useLanguage } from './hooks/useLanguage';

const IncidentsPage = lazy(() => import('./pages/IncidentsPage').then((m) => ({ default: m.IncidentsPage })));
const MethodologyPage = lazy(() => import('./pages/MethodologyPage').then((m) => ({ default: m.MethodologyPage })));
const EmbedConnectivity = lazy(() => import('./pages/EmbedConnectivity').then((m) => ({ default: m.EmbedConnectivity })));
const EmbedNetworkScore = lazy(() => import('./pages/EmbedNetworkScore').then((m) => ({ default: m.EmbedNetworkScore })));
const EmbedComparison = lazy(() => import('./pages/EmbedComparison').then((m) => ({ default: m.EmbedComparison })));

function RouteFallback() {
  return (
    <div
      className="min-h-screen bg-ir-bg flex items-center justify-center"
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <div className="w-8 h-8 border-2 border-ir-blue border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function MainDashboard() {
  const { language, toggleLanguage } = useLanguage();
  const dashboardRefreshRef = useRef<{ refresh: () => void } | null>(null);

  return (
    <ErrorBoundary language={language}>
      <div className="min-h-screen bg-ir-bg text-ir-text font-sans antialiased flex flex-col">
        <GrafanaDashboard
          language={language}
          refreshRef={dashboardRefreshRef}
          onLanguageToggle={toggleLanguage}
        />
        <Footer language={language} />
      </div>
    </ErrorBoundary>
  );
}

function IncidentsRoute() {
  const { language, toggleLanguage } = useLanguage();
  return (
    <ErrorBoundary language={language}>
      <Suspense fallback={<RouteFallback />}>
        <div className="min-h-screen bg-ir-bg text-ir-text font-sans antialiased flex flex-col">
          <IncidentsPage language={language} onLanguageToggle={toggleLanguage} />
          <Footer language={language} />
        </div>
      </Suspense>
    </ErrorBoundary>
  );
}

function MethodologyRoute() {
  const { language, toggleLanguage } = useLanguage();
  return (
    <ErrorBoundary language={language}>
      <Suspense fallback={<RouteFallback />}>
        <div className="min-h-screen bg-ir-bg text-ir-text font-sans antialiased flex flex-col">
          <MethodologyPage language={language} onLanguageToggle={toggleLanguage} />
          <Footer language={language} />
        </div>
      </Suspense>
    </ErrorBoundary>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route
          path="/embed/connectivity"
          element={
            <Suspense fallback={<RouteFallback />}>
              <EmbedConnectivity />
            </Suspense>
          }
        />
        <Route
          path="/embed/network-score"
          element={
            <Suspense fallback={<RouteFallback />}>
              <EmbedNetworkScore />
            </Suspense>
          }
        />
        <Route
          path="/embed/comparison"
          element={
            <Suspense fallback={<RouteFallback />}>
              <EmbedComparison />
            </Suspense>
          }
        />
        <Route path="/incidents" element={<IncidentsRoute />} />
        <Route path="/methodology" element={<MethodologyRoute />} />
        <Route path="/*" element={<MainDashboard />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;